import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tiReportService from '../../services/TIReportService.js';
import TIReportFileCabinet from './TIReportFileCabinet.js';
import { FileSearch, Pencil, ArrowLeft, Download, CheckCircle, XCircle, FolderOpen, DollarSign } from "lucide-react";
import { getViewPageActionButtons, isViewPageActionDisabled } from '../../utils/roleBasedUIHelpers.js';
import { ROLES } from '../../utils/permissions.js';

const ViewTIReport = () => {
  const { tiReportId: reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFileCabinet, setShowFileCabinet] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isViewOnlyRole = isClient || isAccountant;
  
  // Get action buttons based on role
  const actionButtons = getViewPageActionButtons(userRole);
  const isActionDisabled = (action) => isViewPageActionDisabled(userRole, action);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await tiReportService.getById(reportId);
      setReport(response.data);
    } catch (err) {
      Alert.error('Failed to load TI report details');
      console.error(err);
      navigate('/ti-reports');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Paid',
        icon: CheckCircle
      },
      'unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-700',
        label: 'Unpaid',
        icon: XCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: 'Payment Pending',
        icon: DollarSign
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const handleDownloadCertificate = async () => {
    try {
      const blob = await tiReportService.generateCertificate(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ti_report_${(report.tank_no || report.ti_id).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Certificate downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download certificate');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading TI report details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <FileSearch className="w-5 h-5 text-buttonBg" />
                      TI Report Details
                    </h1>
                  </div>
                  <button onClick={() => navigate('/ti-reports')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0" />
                    <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                    <span className="relative z-10">Back to TI Reports</span>
                  </button>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">

                  {/* Profile Header */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {report.ti_id}
                        </h2>
                        {!isClient && (
                          <div className="flex flex-wrap items-center gap-2">
                            {report.send_to_dm && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <CheckCircle className="w-3 h-3" />
                                Sent to Documentation Manager
                              </span>
                            )}
                            {report.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Published to Client
                              </span>
                            )}
                            {!report.send_to_dm && !report.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3" />
                                Draft
                              </span>
                            )}
                            {report.payment_status && getPaymentStatusBadge(report.payment_status)}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        {actionButtons.view && (
                          <button onClick={() => setShowFileCabinet(true)}
                            disabled={isActionDisabled('view')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('view') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0" />
                            <FolderOpen className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">File Cabinet</span>
                          </button>
                        )}

                        {actionButtons.downloadPDF && (
                          <button onClick={handleDownloadCertificate}
                            disabled={isActionDisabled('downloadPDF') || (isClient && !report.published)}
                            title={isClient && !report.published ? 'PDF download is only available after publishing' : ''}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${(isActionDisabled('downloadPDF') || (isClient && !report.published)) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0" />
                            <Download className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Download Certificate</span>
                          </button>
                        )}

                        {actionButtons.edit && (
                          <button onClick={() => navigate(`/ti-reports/edit/${reportId}`)}
                            disabled={isActionDisabled('edit')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('edit') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0" />
                            <Pencil className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Edit Report</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">

                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">TI ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.ti_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Date of Survey</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.date_of_survey)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Inspector</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.inspected_by_name}</p>
                        </div>
                        {report.location && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Location</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                              {report.location.city}, {report.location.state}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tank Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Tank Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.tank_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.tank_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manufacture Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.manufacture_date)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">MGW (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.maximum_gross_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tare Weight (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.tare_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Capacity (Ltr)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.capacity_ltr || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.csc || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Depot Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.depot_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">LED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.led)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">NED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.ned)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Inspection Remarks */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Inspection Remarks</h3>
                      <div className="space-y-4">
                        {report.external_remarks && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">External Remarks</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{report.external_remarks}</p>
                          </div>
                        )}
                        {report.internal_remarks && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Internal Remarks</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{report.internal_remarks}</p>
                          </div>
                        )}
                        {report.ancillary_fittings && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Ancillary Fittings</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{report.ancillary_fittings}</p>
                          </div>
                        )}
                        {report.remarks && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">General Remarks</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{report.remarks}</p>
                          </div>
                        )}
                        {!report.external_remarks && !report.internal_remarks && !report.ancillary_fittings && !report.remarks && (
                          <p className="text-sm text-gray-500 italic">No remarks recorded.</p>
                        )}
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">System Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{report.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(report.updated_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Published Status</label>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            report.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {report.published ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {report.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* File Cabinet Modal */}
      {showFileCabinet && report && (
        <TIReportFileCabinet
          tiReportId={report.ti_id}
          clientId={report.client_id}
          onClose={() => setShowFileCabinet(false)}
        />
      )}
    </div>
  );
};

export default ViewTIReport;
